import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';

import { generateUploadFileFormField } from './generation/form-field';
import { uploadFileV2Migration } from './migrations/002-upload-file.migration';
import { uploadFileV3Migration } from './migrations/003-upload-file.migration';
import { uploadFileV4PaddingToMarginMigration } from './migrations/004-upload-file-padding-to-margin.migration';
import { UploadFileComponent } from './upload-file.component';
import { DEFAULT_UPLOAD_FILE_COLUMNS, type UploadFilePropsModel } from './upload-file.model';
import { UploadFilePropsBuilder } from './upload-file.props-builder';
import {
  getUploadFilePropsConfig,
  UPLOAD_FILE_DEFAULT_WIDTH,
  UPLOAD_FILE_MIN_SIZE,
} from './upload-file.props-config';

export { transformToUploadFile } from './upload-file.transforms';
export type UploadFileDef = WithProps<UploadFilePropsModel> & WithIoConfig;
export type UploadFileElement = CanvasElementFrom<'uploadFile'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    uploadFile: UploadFileDef;
  }
}

registerElementDefinition({
  type: 'uploadFile',
  componentType: UploadFileComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'File Upload',
  width: { mode: 'value', value: UPLOAD_FILE_DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: UPLOAD_FILE_MIN_SIZE, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getUploadFilePropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  toolbar: ['triggerClick'],
  tags: ['Image', 'Photo'],
  propsBuilder: UploadFilePropsBuilder,
  generation: {
    formField: generateUploadFileFormField,
  },
  groups: ['advanced'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      readonly: 'content.button.readonly',
    },
  },
  version: 4,
  migrations: {
    2: uploadFileV2Migration,
    3: uploadFileV3Migration,
    4: uploadFileV4PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'File URL', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          columns: DEFAULT_UPLOAD_FILE_COLUMNS,
          id: '',
          name: 'fileUpload',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'uploadFile'> {
  return {
    content: {
      button: {
        label: 'Upload File',
        icon: 'arrow_up_full',
        readonly: false,
      },
      fileFormats: {
        enabled: true,
        csv: false,
        docs: false,
        image: false,
        video: false,
        audio: false,
      },
      options: {
        multiple: false,
        saveAs: 'url',
        capture: {
          enabled: false,
          format: 'image',
          defaultCamera: 'user',
          saveAsMp4: false,
        },
      },
      title: {
        enabled: false,
        label: 'File Upload',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.labelFontWeight',
        fontStyle: [],
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      button: {
        outlineWidth: '1px',
        radius: '$t.radius',
      },
      dragDrop: {
        label: { color1: '$t.title', selectorType: 'solid' },
        action: { color1: '$t.accent', selectorType: 'solid' },
        align: 'center',
        font: {
          style: [],
          fontFamily: '$t.fontFamily',
          lineHeight: '$t.lineHeight',
          fontSize: '$t.fontSize',
          fontWeight: '$t.labelFontWeight' as FontWeightOption,
        },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        outlineWidth: '1px',
        radius: '$t.radius',
      },
      upload: {
        // label:
        label: { color1: '$t.complementary', selectorType: 'solid' },
        align: 'center',
        font: {
          style: [],
          fontFamily: '$t.fontFamily',
          lineHeight: '$t.lineHeight',
          fontSize: '$t.fontSize',
          fontWeight: '$t.labelFontWeight' as FontWeightOption,
        },
        iconSize: '24px',
        // button:
        fill: { color1: '$t.accent', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'none' },
      },
      uploaded: {
        label: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'uploadFile'> {
  return {
    content: {
      button: {
        icon: 'fas-circle-arrow-up',
      },
      title: {
        width: '35%',
      },
    },
    style: {
      upload: {
        label: { color1: '$t.accentLabel', selectorType: 'solid' },
        iconSize: '$t.buttonIconSize',
      },
    },
  };
}
